/** @format */
import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FudisSelectOption, FudisValidators } from '@funidata/ngx-fudis';
import { TranslocoService } from '@ngneat/transloco';
import { MaxLength } from 'common-typescript/constants';
import { OpenUniversityUserBasicDetails, SuomiFiUserBasicDetails, Urn } from 'common-typescript/types';
import { combineLatest, map, Observable, take } from 'rxjs';
import { SisFormBuilder } from 'sis-components';

export interface SuomiFiUserDetailsForm {
    secondaryEmail: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
    citizenshipUrn: FormControl<FudisSelectOption<Urn> | null>;
    municipalityUrn: FormControl<FudisSelectOption<Urn> | null>;
    motherTongueUrn: FormControl<FudisSelectOption<Urn> | null>;
    preferredLanguageUrn: FormControl<FudisSelectOption<Urn> | null>;
    genderUrn: FormControl<FudisSelectOption<Urn> | null>;
}

@Injectable({ providedIn: 'root' })
export class SuomiFiUserDetailsFormService {
    private readonly fb = inject(SisFormBuilder);
    private readonly translocoService = inject(TranslocoService);

    buildForm(suomiFiUserBasicDetails: SuomiFiUserBasicDetails): Observable<FormGroup<SuomiFiUserDetailsForm>> {
        return combineLatest([
            this.translocoService.selectTranslate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.EMAIL'),
            this.translocoService.selectTranslate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.REQUIRED'),
            this.translocoService.selectTranslate('SIS_COMPONENTS.COMMON_VALIDATION_ERRORS.MAX_LENGTH', {
                maxLength: MaxLength.MAX_SHORT_STRING_LENGTH,
            }),
        ]).pipe(
            take(1),
            map(([emailError, requiredError, maxLengthError]) => {
                // URN fields are only collected (and required) when the user isn't already a valid student.
                const urnValidators = suomiFiUserBasicDetails.isExistingAndValidStudent ? [] : [FudisValidators.required(requiredError)];
                return this.fb.group<SuomiFiUserDetailsForm>({
                    secondaryEmail: this.fb.control(suomiFiUserBasicDetails?.secondaryEmail, [
                        FudisValidators.email(emailError),
                        FudisValidators.required(requiredError),
                        FudisValidators.maxLength(MaxLength.MAX_SHORT_STRING_LENGTH, maxLengthError),
                    ]),
                    phoneNumber: this.fb.control(suomiFiUserBasicDetails?.phoneNumber, [
                        FudisValidators.required(requiredError),
                        FudisValidators.maxLength(MaxLength.MAX_SHORT_STRING_LENGTH, maxLengthError),
                    ]),
                    citizenshipUrn: this.fb.control(null, urnValidators),
                    municipalityUrn: this.fb.control(null, urnValidators),
                    motherTongueUrn: this.fb.control(null, urnValidators),
                    preferredLanguageUrn: this.fb.control(null, urnValidators),
                    genderUrn: this.fb.control(null, urnValidators),
                });
            }),
        );
    }

    getOpenUniversityUserBasicDetails(
        form: FormGroup<SuomiFiUserDetailsForm>,
        suomiFiUserBasicDetails: SuomiFiUserBasicDetails,
    ): OpenUniversityUserBasicDetails {
        const value = form.getRawValue();
        return {
            ...suomiFiUserBasicDetails,
            secondaryEmail: value.secondaryEmail,
            phoneNumber: value.phoneNumber,
            citizenshipUrns: value.citizenshipUrn?.value ? [value.citizenshipUrn.value] : [],
            municipalityUrn: value.municipalityUrn?.value,
            motherTongueUrn: value.motherTongueUrn?.value,
            preferredLanguageUrn: value.preferredLanguageUrn?.value,
            genderUrn: value.genderUrn?.value,
            dateOfBirth: undefined, // only for staff, should be refactored away (use a class other than OpenUniversityUserBasicDetails)
            personalIdentityCode: undefined, // only for staff, should be refactored away (use a class other than OpenUniversityUserBasicDetails)
        };
    }
}
